<template>
  <div class="login-form">
    <!-- Stage 1: credentials -->
    <template v-if="stage === 'credentials'">
      <p class="text-xxl font-bold mb-4">
        {{ $t('ui.common.log_in') }}
      </p>
      <p class="text-secondary">
        {{ $t('ui.common.log_in_moto') }}
      </p>

      <ModalErrors
        v-if="formErrors.length"
        :negative-offset="12"
        :messages="formErrors"
      />

      <Form
        id="sign-in-form"
        :validation-schema="validationSchema"
        class="relative"
        @submit="logIn"
      >
        <div
          v-show="showLoader"
          class="absolute w-full h-full bg-surface z-100 top-0 items-center justify-center flex"
        >
          <Spinner width="100" class="fill-brand" />
        </div>

        <Alert
          v-if="isExpiredSession"
          id="sign-in-session-expired"
          type="warning"
          align="center"
          class="mt-4"
        >
          {{ $t('messages.auth.your_session_has_expired') }}
        </Alert>

        <BasicInput
          id="sign-in-email"
          v-model="email"
          name="email"
          :label="$t('ui.common.email')"
          placeholder="john@company.com"
          autocomplete="email"
          required
          hide-asterisk
          class="mt-6"
        />

        <div class="relative">
          <BasicInput
            id="sign-in-password"
            v-model="password"
            name="password"
            type="password"
            :label="$t('ui.common.password')"
            autocomplete="current-password"
            required
            hide-asterisk
            class="mt-1"
          />
          <router-link
            id="sign-in-forgot-password"
            class="absolute top-0 right-0 text-xs text-brand font-medium"
            :to="{ name: 'ForgotPassword' }"
          >
            {{ $t('ui.common.forgot_password') }}
          </router-link>
        </div>

        <div class="mt-3">
          <Checkbox
            v-model="privacyModeEnabled"
            :label="$t('ui.auth.login_with_privacy_enabled')"
          />
        </div>

        <div class="mt-6">
          <Button
            id="sign-in-submit"
            type="primary"
            button-type="submit"
            :disabled="submittingLogin || captchaAwaitingToken"
            :loading="captchaSolving"
            class="w-1/2 ml-auto"
          >
            {{ $t('ui.common.log_in') }}
          </Button>
        </div>

        <div ref="turnstileContainer" class="mt-4 flex justify-center" />

        <Alert
          v-if="captchaLoadFailed"
          id="sign-in-captcha-unavailable"
          type="warning"
          align="center"
          class="mt-4"
        >
          <span v-html="captchaUnavailableMessage" />
        </Alert>
      </Form>
    </template>

    <!-- Stage 2: 2FA challenge -->
    <template v-else-if="stage === 'twofa'">
      <p class="text-xxl font-bold mb-4">
        {{ $t('ui.auth.two_factor.title') }}
      </p>
      <p class="text-secondary">
        {{ $t('ui.auth.two_factor.prompt') }}
      </p>

      <ModalErrors
        v-if="formErrors.length"
        :negative-offset="12"
        :messages="formErrors"
      />

      <div class="relative mt-6">
        <div
          v-show="showLoader"
          class="absolute w-full h-full bg-surface z-100 top-0 items-center justify-center flex"
        >
          <Spinner width="100" class="fill-brand" />
        </div>

        <OtpInput v-model="twoFactorCode" @complete="verifyTwoFactor" />

        <div class="flex justify-between items-center mt-6">
          <Button
            id="sign-in-2fa-back"
            icon="Back"
            type="secondary"
            :disabled="showLoader"
            @click="backToCredentials"
          >
            {{ $t('ui.common.back') }}
          </Button>
          <Button
            id="sign-in-2fa-verify"
            type="primary"
            :disabled="twoFactorCode.length !== 6"
            :loading="showLoader"
            @click="verifyTwoFactor"
          >
            {{ $t('ui.auth.two_factor.verify') }}
          </Button>
        </div>

        <div class="mt-8 text-center">
          <button
            id="sign-in-2fa-lost-access"
            type="button"
            class="text-xs text-brand font-medium"
            @click="showLostAccessHint = !showLostAccessHint"
          >
            {{ $t('ui.auth.two_factor.lost_access') }}
          </button>
          <p
            v-if="showLostAccessHint"
            class="text-xs text-secondary mt-2"
            v-html="lostAccessHint"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, markRaw } from 'vue'
import { object, string } from 'yup'

import { retrieveErrorsMessages } from '@/helpers/errors'
import UserService from '@/services/user/user'
import { SUPPORT_EMAIL } from '@/constants/support'

import Spinner from '@/assets/loaders/Spinner.svg?component'
import BasicInput from '@/components/controls/BasicInput'
import Button from '@/components/controls/Button'
import Checkbox from '@/components/controls/Checkbox'
import OtpInput from '@/components/controls/OtpInput'
import Alert from '@/components/misc/Alert/Alert.vue'
import ModalErrors from '@/components/misc/Modal/ModalErrors.vue'
import { Form } from 'vee-validate'
import { useUserStore } from '@/stores/user'
import { useTurnstile } from '@/composables/turnstile'

type Stage = 'credentials' | 'twofa'

// Backstop for a mounted widget that silently delivers no token.
// Turnstile's own error verdict can take very long to arrive (blocked
// requests that hang until browser timeouts, internal auto-retries), so
// the captcha_unavailable alert doesn't wait for it. A healthy
// non-interactive challenge solves within a few seconds even on a slow
// connection, so this long without a token reads as blocked; an
// interactive challenge (user must click) suspends the timer via
// onInteractivity — waiting on the user is not slowness. A token that
// still arrives retracts the alert.
const CAPTCHA_WARN_AFTER_MS = 30_000

export default defineComponent({
  components: {
    Alert,
    BasicInput,
    Button,
    Checkbox,
    Form,
    OtpInput,
    Spinner,
    ModalErrors,
  },

  setup() {
    const userStore = useUserStore()
    const turnstile = useTurnstile()

    return {
      userStore,
      turnstile,
    }
  },

  data() {
    return {
      showLoader: false,
      submittingLogin: false,
      isExpiredSession: false,
      email: '',
      password: '',
      privacyModeEnabled: false,
      formErrors: [] as string[],

      // Whether the backend enforces the login captcha in this
      // environment (GET /api/v2/root/login-config — one ansible setting
      // drives both repos). Defaults to true so every pre-fetch and
      // fetch-failure state fails CLOSED: rendering a widget the backend
      // ignores costs a badge; skipping a widget the backend requires
      // strands the user at a tokenless login it refuses.
      captchaEnabled: true,
      captchaLoadFailed: false,
      // Mirrors the composable's token availability (onTokenAvailability
      // callback) so the template can gate the submit button on it.
      captchaHasToken: false,
      captchaWarnTimer: null as ReturnType<typeof setTimeout> | null,

      stage: 'credentials' as Stage,
      challengeId: null as string | null,
      twoFactorCode: '',
      showLostAccessHint: false,

      validationSchema: markRaw(
        object({
          email: string().required().email(),
          password: string().required(),
        })
      ),
    }
  },

  computed: {
    lostAccessHint(): string {
      return this.$t('ui.auth.two_factor.lost_access_hint', {
        email: `<a class="text-brand font-medium" href="mailto:${SUPPORT_EMAIL}">${SUPPORT_EMAIL}</a>`,
      })
    },
    captchaUnavailableMessage(): string {
      return this.$t('ui.auth.captcha_unavailable', {
        email: `<a class="text-brand font-medium" href="mailto:${SUPPORT_EMAIL}">${SUPPORT_EMAIL}</a>`,
      })
    },
    captchaAwaitingToken(): boolean {
      // No solved token, no submit — whenever the captcha is enabled.
      // The backend refuses a tokenless login, so offering the button
      // without a token only manufactures a guaranteed failure. When the
      // widget failed to load, the captcha_unavailable alert tells the
      // user how to unblock it (and whom to contact); the button stays
      // locked until a token actually exists.
      return this.captchaEnabled && !this.captchaHasToken
    },
    captchaSolving(): boolean {
      // The locked submit shows a spinner while the widget is still
      // expected to deliver a token, so the wait reads as activity
      // rather than a dead button. Once the failure alert is up, a
      // spinner would falsely promise progress — plain disabled + the
      // alert take over.
      return this.captchaAwaitingToken && !this.captchaLoadFailed
    },
  },

  mounted() {
    this.getUrlQuery()
    this.formErrors = UserService.getAuthErrors()
    this.initCaptcha()
  },

  beforeUnmount() {
    this.clearCaptchaWarnTimer()
    this.turnstile.remove()
  },

  methods: {
    getUrlQuery() {
      if (this.$route.query.expired) {
        this.isExpiredSession = true
      }
    },
    async logIn() {
      if (this.submittingLogin) {
        return
      }
      this.submittingLogin = true

      this.isExpiredSession = false
      this.formErrors = []
      this.showLoader = true

      // Synchronous handout of the token the visible widget already
      // solved. The submit button is gated on it (captchaAwaitingToken),
      // but a programmatic submit — password managers auto-logging-in
      // via requestSubmit() — bypasses a disabled button, so the
      // invariant is enforced here too: no token, no login request. The
      // bail-out unlocks the form; the button opens once the still-
      // solving widget delivers, and a retry then goes out with a token.
      const captchaToken = this.turnstile.getToken()
      if (this.captchaEnabled && !captchaToken) {
        this.showLoader = false
        this.submittingLogin = false
        return
      }

      try {
        const result = await this.userStore.login(
          this.email,
          this.password,
          captchaToken
        )

        if (result.requires_2fa) {
          if (!result.challenge_id) {
            throw new Error(this.$t('ui.auth.two_factor.invalid_response'))
          }
          this.challengeId = result.challenge_id
          this.twoFactorCode = ''
          this.stage = 'twofa'
          return
        }

        await this.finishLoginRedirect()
      } catch (err) {
        this.formErrors = retrieveErrorsMessages(err)
      } finally {
        this.showLoader = false
        this.submittingLogin = false
      }
    },

    async verifyTwoFactor() {
      if (this.twoFactorCode.length !== 6 || !this.challengeId) return

      this.showLoader = true
      this.formErrors = []

      try {
        await this.userStore.verifyLogin2fa(
          this.challengeId,
          this.twoFactorCode
        )
        await this.finishLoginRedirect()
      } catch (err) {
        const messages = retrieveErrorsMessages(err)
        this.formErrors =
          messages.length > 0
            ? messages
            : [this.$t('ui.auth.two_factor.invalid_code')]
        this.twoFactorCode = ''
      } finally {
        this.showLoader = false
      }
    },

    async finishLoginRedirect() {
      const resolved = this.userStore.setAutoSession(false)

      if (resolved) {
        if (this.privacyModeEnabled) {
          await this.userStore.saveUserAdditionalSettings({
            privacy_mode: this.privacyModeEnabled,
          })
        }
        await this.$router.push('/')
      } else {
        await this.$router.push({
          name: 'AccountSelection',
          query: { privacy: this.privacyModeEnabled.toString() },
        })
      }
    },

    backToCredentials() {
      this.stage = 'credentials'
      this.challengeId = null
      this.twoFactorCode = ''
      this.formErrors = []
      this.showLostAccessHint = false
      this.password = ''

      // The credentials-stage container is a fresh DOM node (v-if
      // recreated it), so the widget needs to be re-rendered into it.
      this.renderCaptcha()
    },

    clearCaptchaWarnTimer() {
      if (this.captchaWarnTimer) {
        clearTimeout(this.captchaWarnTimer)
        this.captchaWarnTimer = null
      }
    },

    startCaptchaWarnTimer() {
      // Preemptive no-token backstop (see CAPTCHA_WARN_AFTER_MS): token
      // arrival, an interactive challenge and teardown each cancel it
      // via clearCaptchaWarnTimer().
      this.clearCaptchaWarnTimer()
      this.captchaWarnTimer = setTimeout(() => {
        this.captchaWarnTimer = null
        if (!this.captchaHasToken) {
          this.captchaLoadFailed = true
        }
      }, CAPTCHA_WARN_AFTER_MS)
    },

    async initCaptcha() {
      try {
        const { captcha_enabled } = await UserService.getLoginConfig()
        this.captchaEnabled = captcha_enabled
      } catch {
        // Fail closed (see the captchaEnabled data comment): the server
        // may be enforcing, so an unreachable config endpoint must not
        // strip the widget.
        this.captchaEnabled = true
      }
      if (this.captchaEnabled) {
        this.renderCaptcha()
      }
    },

    renderCaptcha(): Promise<boolean> {
      if (!this.captchaEnabled) {
        // Nothing to mount and nothing to reset — the 2FA back-navigation
        // calls this unconditionally.
        return Promise.resolve(false)
      }
      this.turnstile.remove()
      // Fresh attempt, fresh verdict — a stale failure from a previous
      // widget must not keep the alert up over a working one.
      this.clearCaptchaWarnTimer()
      this.captchaLoadFailed = false
      return this.$nextTick().then(async () => {
        const container = this.$refs.turnstileContainer as
          | HTMLElement
          | undefined
        if (!container) {
          return false
        }
        const rendered = await this.turnstile.render(container, {
          onTokenAvailability: (hasToken: boolean) => {
            this.captchaHasToken = hasToken
            if (hasToken) {
              // A delivered token proves the challenge works: retract any
              // (preemptive or error-driven) alert and let the token
              // gate take over. The warn timer must be cancelled, not
              // just outrun — after this token is consumed by a submit,
              // a stale timer firing mid-re-solve would raise the alert
              // over a healthy widget.
              this.clearCaptchaWarnTimer()
              this.captchaLoadFailed = false
            }
          },
          onError: () => {
            // The widget mounted but the challenge itself failed —
            // challenge requests blocked (adblocker) or network failure.
            // Without this the submit button would sit locked on a token
            // that never comes, with no explanation of why.
            this.captchaLoadFailed = true
          },
          onInteractivity: (interactive: boolean) => {
            if (interactive) {
              // Cloudflare went interactive: the widget is loaded,
              // working, and waiting for the user's click — its own UI
              // explains that wait. No-token time means nothing here, so
              // the warn timer stands down and an alert already up
              // (which would now be wrong) comes down.
              this.clearCaptchaWarnTimer()
              this.captchaLoadFailed = false
            } else if (!this.captchaHasToken) {
              // Interaction finished; the token should follow within
              // seconds. A fresh timer catches a post-click hang.
              this.startCaptchaWarnTimer()
            }
          },
        })
        // captchaLoadFailed drives the captcha_unavailable alert: the
        // submit stays token-locked either way, so a user whose widget
        // can't load (adblocker, network) must learn why login is
        // unavailable and how to unblock it. Only ever raised here, never
        // cleared — onError may already have fired during the mount.
        if (!rendered) {
          this.captchaLoadFailed = true
        } else {
          this.startCaptchaWarnTimer()
        }
        return rendered
      })
    },
  },
})
</script>
